const STORAGE_KEY = "quizmate_files";
const SESSION_STORAGE_KEY = "quizmate_session_files";

// Dynamically import mammoth for DOCX processing
let mammoth;
if (typeof window !== "undefined") {
  import("mammoth")
    .then((module) => {
      mammoth = module;
    })
    .catch((err) => console.warn("Mammoth not available:", err));
}

export const saveFileToStorage = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const base64 = reader.result.split(",")[1];
        const newFile = {
          id: generateId(),
          name: file.name,
          type: file.type,
          size: file.size,
          content: base64,
          createdAt: new Date().toISOString(),
          isSessionFile: false,
          textContent: null, // Will be populated for DOC/DOCX files
        };

        // Extract text content for DOC/DOCX files
        const isDocFile =
          file.type.includes("msword") ||
          file.type.includes("officedocument.wordprocessing") ||
          file.name.toLowerCase().endsWith(".doc") ||
          file.name.toLowerCase().endsWith(".docx");

        if (isDocFile) {
          try {
            const arrayBuffer = base64ToArrayBuffer(base64);
            console.log(
              "Processing DOC/DOCX file:",
              file.name,
              "Type:",
              file.type
            );

            mammoth
              .extractRawText({ arrayBuffer })
              .then((result) => {
                if (result.value && result.value.trim().length > 0) {
                  newFile.textContent = result.value;
                  console.log("Successfully extracted text from", file.name);
                } else {
                  newFile.textContent = `Document: ${file.name}

File uploaded successfully.
Size: ${formatFileSize(file.size)}
Uploaded: ${new Date().toLocaleDateString()}

No text content could be extracted from this document. This may be due to:
- The document being password protected
- The document containing only images or formatting
- The document being corrupted or in an unsupported format`;
                }
                saveFile(newFile, resolve, reject);
              })
              .catch((err) => {
                console.warn("Failed to extract text from DOC:", err);
                newFile.textContent = `Document: ${file.name}

File uploaded successfully.
Size: ${formatFileSize(file.size)}
Uploaded: ${new Date().toLocaleDateString()}

Text extraction failed: ${
                  err.message || "Unable to process this document format"
                }`;
                saveFile(newFile, resolve, reject);
              });
          } catch (err) {
            console.warn("Error processing DOC file:", err);
            newFile.textContent = `Document: ${file.name}

File uploaded successfully.
Size: ${formatFileSize(file.size)}
Uploaded: ${new Date().toLocaleDateString()}

Error processing document: ${err.message}`;
            saveFile(newFile, resolve, reject);
          }
        } else {
          saveFile(newFile, resolve, reject);
        }
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

const saveFile = (newFile, resolve, reject) => {
  try {
    // Check file size before attempting to save
    const fileSizeMB = newFile.size / (1024 * 1024);

    // For files larger than 50MB, reject
    if (fileSizeMB > 50) {
      reject(
        new Error(
          `File too large (${fileSizeMB.toFixed(
            2
          )}MB) to store. Maximum supported size is 50MB.`
        )
      );
      return;
    }

    // For files larger than 5MB, use sessionStorage directly
    if (fileSizeMB > 5) {
      console.log(
        `Large file detected: ${fileSizeMB.toFixed(2)}MB, using sessionStorage`
      );
      try {
        const sessionFiles = getSessionFiles();
        sessionFiles.push(newFile);
        sessionStorage.setItem(
          SESSION_STORAGE_KEY,
          JSON.stringify(sessionFiles)
        );
        newFile.isSessionFile = true;
        resolve(newFile);
        return;
      } catch (sessionError) {
        reject(
          new Error(
            `File too large (${fileSizeMB.toFixed(
              2
            )}MB) to store. Maximum supported size is 50MB.`
          )
        );
        return;
      }
    }

    // Try to save to localStorage
    const files = getFilesFromStorage();

    // Check for duplicate file names and remove if exists
    const existingIndex = files.findIndex((f) => f.name === newFile.name);
    if (existingIndex !== -1) {
      console.log(`⚠️ File '${newFile.name}' already exists. Replacing...`);
      files[existingIndex] = newFile; // Replace existing file
    } else {
      files.push(newFile);
    }

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(files));
      resolve(newFile);
    } catch (storageError) {
      if (storageError.name === "QuotaExceededError") {
        console.warn(
          "localStorage quota exceeded, falling back to sessionStorage"
        );

        // Fallback to session storage
        try {
          const sessionFiles = getSessionFiles();
          // Check for duplicates in session storage too
          const sessionIndex = sessionFiles.findIndex(
            (f) => f.name === newFile.name
          );
          if (sessionIndex !== -1) {
            sessionFiles[sessionIndex] = newFile;
          } else {
            sessionFiles.push(newFile);
          }
          sessionStorage.setItem(
            SESSION_STORAGE_KEY,
            JSON.stringify(sessionFiles)
          );
          newFile.isSessionFile = true;
          resolve(newFile);
        } catch (sessionError) {
          reject(
            new Error(
              `Storage quota exceeded. File size: ${fileSizeMB.toFixed(
                2
              )}MB. Please try a smaller file or clear some stored files.`
            )
          );
        }
      } else {
        reject(storageError);
      }
    }
  } catch (error) {
    reject(error);
  }
};

export const getFilesFromStorage = () => {
  try {
    const files = localStorage.getItem(STORAGE_KEY);
    return files ? JSON.parse(files) : [];
  } catch (error) {
    console.error("Error getting files:", error);
    return [];
  }
};

export const getSessionFiles = () => {
  try {
    const files = sessionStorage.getItem(SESSION_STORAGE_KEY);
    return files ? JSON.parse(files) : [];
  } catch (error) {
    console.error("Error getting session files:", error);
    return [];
  }
};

export const deleteFileFromStorage = (fileId) => {
  try {
    // Try to delete from localStorage
    const files = getFilesFromStorage();
    const updatedFiles = files.filter((file) => file.id !== fileId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedFiles));

    // Also try to delete from sessionStorage
    const sessionFiles = getSessionFiles();
    const updatedSessionFiles = sessionFiles.filter(
      (file) => file.id !== fileId
    );
    sessionStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify(updatedSessionFiles)
    );
  } catch (error) {
    console.error("Error deleting file:", error);
    throw new Error("Failed to delete file");
  }
};

export const base64ToFile = (base64String, filename, type) => {
  try {
    let base64Data = base64String;
    let mimeType = type || "application/octet-stream";

    // Handle base64 strings with data URL prefix
    if (typeof base64String === "string" && base64String.includes(",")) {
      const arr = base64String.split(",");
      const mimeMatch = arr[0].match(/:(.*?);/);
      if (mimeMatch) {
        mimeType = mimeMatch[1];
      }
      base64Data = arr[1] || arr[0]; // Fallback to full string if no comma
    }

    // Ensure we have valid base64 data
    if (!base64Data || typeof base64Data !== "string") {
      throw new Error("Invalid base64 data");
    }

    // Clean base64 data (remove whitespace, newlines)
    base64Data = base64Data.replace(/\s/g, "");

    // Validate base64 data length
    if (base64Data.length === 0) {
      throw new Error("Empty base64 data");
    }

    const bstr = atob(base64Data);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mimeType });
  } catch (error) {
    console.error("Error converting base64 to file:", error);
    // Return a minimal valid file with error message
    const errorContent = `Error: Unable to process file ${filename}`;
    const blob = new Blob([errorContent], { type: "text/plain" });
    return new File([blob], filename, { type: "text/plain" });
  }
};

const base64ToArrayBuffer = (base64) => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const generateId = () => {
  return Math.random().toString(36).substr(2, 9) + "_" + Date.now();
};

export const getAllFiles = () => {
  const localFiles = getFilesFromStorage();
  const sessionFiles = getSessionFiles();
  return [...localFiles, ...sessionFiles];
};

export const clearAllFiles = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing all files:", error);
    throw new Error("Failed to clear all files");
  }
};
